import { AnswerDTO } from './questions'
import { EncodedCursor } from 'types/definitions'
import { Job as JobProto } from '@taxfyle/api-internal/internal/job_pb'
import { IDecimalAsObject } from '../../utils/grpc-util'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { Consultation as ConsultationProto } from '@taxfyle/api-internal/internal/consultation_pb'

export interface OpportunityDTO {
  id: string
  name: string
  description: string
  specs: string[]
  info_lines: InfoLineDTO[]
  date_deadline: string | null
  date_created: string
  date_modified: string
  date_transmitted: string | null
  provider_cut_amount: number
  workspace: {
    id: string
  }
  legend: {
    id: string
    name: string
    version: number
    price_unit?: string
  }
  client: {
    user_id: string
    given_name?: string
    family_name?: string
    display_name?: string
    picture?: string
  }
}

export interface JobDTO {
  amendments: AmendmentDTO[]
  date_deadline: string | null
  id: string
  short_id: string
  job_specs: string[]
  layerConversation: string
  legend_id: string
  legend_name: string
  legend_version: number
  description?: string
  name?: string
  legend?: any
  members: JobMemberDTO[]
  date_accepted?: string | null
  date_created: string
  date_modified: string
  date_transmitted?: string | null
  date_closed?: string | null
  date_last_activity: string
  coupon_codes?: Array<string>
  coupon_amount?: number
  status: JobStatus
  workspace_id: string
  milestones: MilestoneDTO[]
  owner_team_id?: string | null
  team_ids: Array<string> | null
  requested_pro_id?: string | null
  requested_pro_public_id?: string | null
  info_lines?: InfoLineDTO[]
  answers?: AnswerDTO[]
  resolution?: JobResolution
  primary_amount?: number
  provider_cut_amount: number
  paid_amount?: number
  total?: number
  stripe_charges?: string[]
  payment_info_status?: JobPaymentInfoStatus
  waiting_on: JobWaitingOn
  payout_split?: PayoutSplitDTO | null
  calling_actor_allocation?: number | null
  job_scope_items?: JobScopeItemDTO[]
  billing_type: JobBillingType
  consultation?: ConsultationDTO
  type: JobType
}

export interface JobEventSnapshotDTO {
  amendments: AmendmentDTO[]
  date_deadline: string | null
  id: string
  short_id: string
  job_specs: string[]
  layerConversation: string
  legend_id: string
  legend_name: string
  legend_version: number
  description?: string
  name?: string
  legend?: any
  members: JobMemberDTO[]
  date_accepted?: string | null
  date_created: string
  date_modified: string
  date_transmitted?: string | null
  date_start?: string | null
  date_closed?: string | null
  date_last_activity: string
  coupon_codes?: Array<string>
  status: JobStatus
  workspace_id: string
  milestones: MilestoneDTO[]
  owner_team_id?: string | null
  team_ids: Array<string> | null
  requested_pro_id?: string | null
  requested_pro_public_id?: string | null
  info_lines?: InfoLineDTO[]
  answers?: AnswerDTO[]
  resolution?: JobResolution
  primary_amount?: number
  admin_fee_amount: number
  provider_cut_amount: number
  paid_amount?: number
  total?: number
  stripe_charges?: string[]
  payment_info_status?: JobPaymentInfoStatus
  waiting_on: JobWaitingOn
  payout_split?: PayoutSplitDTO | null
  calling_actor_allocation?: number | null
  job_scope_items?: JobScopeItemDTO[]
  billing_type: JobBillingType
  total_price?: number
  total_pro_cut?: number
  job_scope_price?: number
}

export interface PayoutSplitDTO {
  date_allocated_manually: string | null
  allocated_manually_by_public_id: string | null
  auto_adjusted: boolean
  date_auto_adjusted: string | null
  allocations: PayoutSplitAllocationDTO[]
}

export interface PayoutSplitAllocationDTO {
  user_public_id: string
  amount: number
  authorized_partial_payout_amount: number
}

export interface AmendmentDTO {
  creator: JobUserDTO
  customer_amount: number
  date_created: string
  description: string
  internal_note: string
  provider_amount: number
  visibility: AmendmentVisibility
}

export interface JobScopeItemDTO {
  id: string
  name: string
  description: string
  quantity: number
  courtesy_quantity?: number
  inventory_item_id?: string
  inventory_item_version_id?: string
  price?: number
  pro_price?: number
  bundle_id: string | null
  custom_item_id: string | null
}

export type AmendmentVisibility = 'PUBLIC' | 'INTERNAL'

export interface InfoLineDTO {
  title: string
  content: string
}

export interface MilestoneDTO {
  id: string
  title: string
  description?: string
  completed?: boolean
  tasks: TaskDTO[]
}

export interface TaskDTO {
  id: string
  title: string
  description?: string
  designee: TaskDesignee
  completed_by_id?: string
  completed_by_public_id?: string
  date_completed?: string
  completed?: boolean
}

export interface JobMemberDTO {
  type: CollaboratorType
  role: CollaboratorRole
  user: JobUserDTO
  team_id?: string | null
}

export interface JobUserDTO {
  user_id: string
  user_public_id: string
  display_name: string
  email: string
  family_name: string
  given_name: string
  picture: string
  referrer_meta?: string
}

/**
 * The possible fields to sort jobs by.
 */
export type JobSortingField =
  | 'name'
  | 'customer_first_name'
  | 'deadline'
  | 'modified'
  | 'waiting_on'
  | 'milestone'
  | 'status'
  | 'date_transmitted'
  | 'date_created'
  | 'date_completed'
  | 'last_activity_date'

export type SearchableJobSortField =
  | 'Title'
  | 'WorkspaceName'
  | 'LegendName'
  | 'ClientTeamName'
  | 'ProTeamName'
  | 'ClientChampionName'
  | 'ProChampionName'
  | 'DateCreated'
  | 'DateSubmitted'
  | 'DateDeadline'
  | 'DateCompleted'
  | 'DateClaimed'
  | 'ProviderCutAmount'
  | 'DateLastActivity'

export type JobSorts = Partial<Record<JobSortingField, 'asc' | 'desc'>>

export enum JobScopeOption {
  All,
  Mine,
}

export interface FindJobsQuery {
  workspace_id?: string
  from_transmitted_date?: string
  to_transmitted_date?: string
  from_closed_date?: string
  to_closed_date?: string
  date_closed?: string
  all?: boolean
  team_id?: string
  $limit?: number
  $sort?: JobSorts
  orderBy?: string
  after?: EncodedCursor
  searchText?: string
  status?: JobStatus | JobStatus[]
  type?: CollaboratorType
  role?: CollaboratorRole
  waiting_on?: JobWaitingOn
  filter?: string
  scope: JobScopeOption
}

export interface FindOpportunitiesQuery {
  limit?: number
  after?: EncodedCursor
  orderBy?: { [field: string]: 'asc' | 'desc' }
  searchText?: string
}

export type JobStatus =
  | 'UNDER_CONSTRUCTION'
  | 'INFO_GATHERING'
  | 'UNCLAIMED'
  | 'CLAIMED'
  | 'CLOSED'
  | 'IDLE'
  | 'ON_HOLD'

export type JobResolution = 'TEST' | 'DONE' | 'CANCELLED' | null

export type JobPaymentInfoStatus = 'NOT_PROVIDED' | 'REQUESTED' | 'PROVIDED'

export type JobWaitingOn = 'Client' | 'Provider' | null

export type JobBillingType = 'STRIPE' | 'EXTERNAL'

export interface PatchActionDTO {
  remove_user?: string
  add_user?: string
  member_type?: CollaboratorType
  reason?: string
}

export interface CreateJobPayload {
  legend_id: string
  legend_version: number
  owner_team_id?: string | null
  answers: AnswerDTO[]
}

export interface UpdateDraftPayload {
  name?: string
  legend_id?: string
  legend_version?: number
  answers?: AnswerDTO[]
  requested_pro_id?: string | null
  coupons_used?: string[]
}

// Yes, I hate it too. The inconsistency.
export type TaskDesignee = 'Client' | 'Provider'
export type CollaboratorType = 'CLIENT' | 'PROVIDER'
export type CollaboratorRole = 'CHAMPION' | 'SUPPORTING'

export interface ProvidePaymentInfoPayload {
  billing_method_id: string
}

// These two AmendmentsProtoDTO and JobProtoDTO will make it easier to
// make the mapping from the job proto to job dto
interface AmendmentsProtoDTO extends JobProto.Amendment.AsObject {
  providerAmount: IDecimalAsObject
  customerAmount: IDecimalAsObject
  dateCreated: Timestamp.AsObject
  creator: JobProto.Member.AsObject
}

interface JobScopeItemProtoDTO extends JobProto.JobScopeItem.AsObject {
  price: IDecimalAsObject
  proPrice: IDecimalAsObject
}

export interface JobProtoDTO extends JobProto.AsObject {
  amendmentsList: AmendmentsProtoDTO[]
  scopeItemsList: JobScopeItemProtoDTO[]
}

export interface ConsultationDTO extends ConsultationProto.AsObject {}

export type TaxJobLink = {
  id: string
}
export type PrepAndReviewLink = { type: 'PREP' | 'REVIEW'; id: string }
export type TaxJobLinks = { prepAndReviewLink: PrepAndReviewLink }

export type Unspecified = { type: 'UNSPECIFIED' }
export type Bookkeeping = { type: 'BOOKKEEPING' }
export type Consultation = { type: 'CONSULTATION' }
export type Tax = { type: 'TAX'; jobLinks: TaxJobLinks | null }
export type Misc = { type: 'MISC' }

export type JobType = Unspecified | Bookkeeping | Tax | Consultation | Misc
